<template>
  <a-icon
    v-observe-visibility="visibilityOptions"
    :icon="icon"
    width="auto"
    height="auto"
    :no-hover="true"
    :original="true"
    class="animated-svg__icon"
  />
</template>

<script>
import { PROP_TYPES, propValidator } from '@/utils/validators'
import AIcon from 'shared/AIcon/index.vue'
import { mapGetters } from 'vuex'

const ROTATION_SENSITIVITY_MULTIPLIER = 0.2
const WAIT_FOR_ELEMENT_TIMEOUT = 10 * 1000

export default {
  name: 'RotatableSvgElementOnScroll',
  components: { AIcon },
  props: {
    icon: propValidator([PROP_TYPES.STRING]),
    animatedDataIds: propValidator([PROP_TYPES.ARRAY]),
    startingRotationDegree: propValidator([PROP_TYPES.NUMBER], false, 0)
  },
  consts: {
    resetComponentDataFields: [
      'animatedElements',
      'animatedElementsInitialTransformComputedValues',
      'rotationDegree'
    ]
  },
  data() {
    return {
      animatedElements: null,

      /**
       * Initial translate styles of the element in the shape of "matrix()"
       */
      animatedElementsInitialTransformComputedValues: [],
      rotationDegree: this.startingRotationDegree,
      isVisible: false,
      visibilityOptions: {
        callback: this.onVisibilityStateChange
      }
    }
  },
  computed: {
    ...mapGetters({
      scrollTop: 'scrollTop'
    }),
    animatedElementSelectors() {
      return this.animatedDataIds.map(dataId => `[data-id="${dataId}"]`)
    }
  },
  watch: {
    scrollTop(newVal, oldVal) {
      if (!this.animatedElements || !this.isVisible) {
        return
      }

      const scrollDifference = newVal - oldVal

      /**
       * Ensures clockwise direction of rotation
       */
      this.rotationDegree += scrollDifference * ROTATION_SENSITIVITY_MULTIPLIER

      this.applyRotation()
    }
  },
  methods: {
    onVisibilityStateChange(isVisible) {
      this.isVisible = isVisible

      if (isVisible) {
        this.setElementsData()
      } else {
        this.$helper.resetComponentData(
          this,
          this.$options.consts.resetComponentDataFields
        )
      }
    },
    async setElementsData() {
      const isElementsFound = await Promise.all(
        this.animatedElementSelectors.map(selector =>
          this.$helper.waitForElAppearsInDom({
            selector,
            timeout: WAIT_FOR_ELEMENT_TIMEOUT
          })
        )
      )

      if (!isElementsFound) return

      this.animatedElements = this.animatedElementSelectors.map(selector =>
        document.querySelector(selector)
      )

      this.setBaseStylesForAnimatedElements()

      this.animatedElementsInitialTransformComputedValues = this.animatedElements.map(
        animatedElement => getComputedStyle(animatedElement).transform
      )

      this.applyRotation()
    },
    setTransformValue({ element, transformStyle }) {
      element.style.transform = transformStyle
    },
    generateTransformValue({ rotateValue, index }) {
      const rotationStyle = `rotate(${rotateValue.toFixed(2)}deg)`

      if (
        !this.animatedElementsInitialTransformComputedValues[index] ||
        this.animatedElementsInitialTransformComputedValues[index] === 'none'
      ) {
        return rotationStyle
      }

      return `${this.animatedElementsInitialTransformComputedValues[index]} ${rotationStyle}`
    },
    setBaseStylesForAnimatedElements() {
      if (!this.animatedElements) return

      this.animatedElements.forEach(element => {
        /**
         * Makes SVG element behave as regular HTML element
         * in terms of transform operations
         * @type {string}
         */
        element.style['transform-box'] = 'fill-box'
        element.style['transform-origin'] = 'center'
      })
    },
    applyRotation() {
      if (!this.animatedElements) return

      this.animatedElements.forEach((element, elementIndex) => {
        this.setTransformValue({
          element,
          transformStyle: this.generateTransformValue({
            index: elementIndex,
            rotateValue: this.rotationDegree
          })
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped></style>
